import type { LatLng, Nullable } from "@/models"
import { LatLngFormatType, formatLatLng } from "@/utils/formatting/formatLatLng"
import { faRuler } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { onUnmounted, ref } from "vue"
import { ShadowContainer, defineComponent, inferProps, type ReactiveComponent } from "vue-utils"
import Icon from "../Icon"
import BootstrapButton from "../form/BootstrapButton"

interface Props {
	isMeasuring: boolean
	startCoords: Nullable<LatLng>
	endCoords: Nullable<LatLng>
	distanceText: string
	resetMeasure(): void
}

const measureDistanceStyles = css`
	position: absolute;
	left: 50%;
	top: 0.35rem;
	transform: translateX(-50%);
	z-index: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	max-width: 38rem;
	text-align: center;

	button {
		background-color: rgba(255, 255, 255, 0.8);
		border-radius: 0.5rem;
		padding: 0.35rem 0.75rem;
	}

	.coords-panel {
		background-color: rgba(255, 255, 255, 0.8);
		border-radius: 0.5rem;
		padding: 0.35rem 0.75rem;
		margin-top: 0.5rem;
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.5rem 1rem;
		cursor: pointer;

		.marker {
			width: 1em;

			&.start {
				filter: hue-rotate(300deg) brightness(125%);
			}

			&.end {
				filter: hue-rotate(70deg);
			}
		}
	}

	@keyframes toast-up {
		20%,
		80% {
			top: -12px;
			opacity: 1;
		}

		100% {
			top: -12px;
			opacity: 0;
		}
	}

	.copy-toast {
		position: absolute;
		top: 8px;
		opacity: 0;
		animation: toast-up 2s cubic-bezier(0.075, 0.82, 0.165, 1) forwards;
		pointer-events: none;

		div {
			padding: 0.5rem 1.2rem;
		}
	}
`

const MeasureDistance: ReactiveComponent<Props> = (props) => {
	const isCopying = ref<boolean>(false)
	const copyTimeoutId = ref<number | undefined>()

	function copyDistanceToClipboard() {
		if (!props.startCoords || !props.endCoords || !props.distanceText) {
			return
		}

		isCopying.value = true

		const formattedStart = formatLatLng(props.startCoords, LatLngFormatType.DegreesDecimalMinutes)
		const formattedEnd = formatLatLng(props.endCoords, LatLngFormatType.DegreesDecimalMinutes)
		const distanceString = `Start: ${formattedStart}; End: ${formattedEnd}; ${props.distanceText}`

		void navigator.clipboard.writeText(distanceString)

		clearTimeout(copyTimeoutId.value)
		copyTimeoutId.value = setTimeout(() => (isCopying.value = false), 2000)
	}

	onUnmounted(() => {
		clearTimeout(copyTimeoutId.value)
	})

	return () => (
		<div class={measureDistanceStyles}>
			<BootstrapButton color="light" class={{ active: props.isMeasuring }} onClick={props.resetMeasure}>
				<Icon icon={faRuler} />
				Measure Distance
			</BootstrapButton>

			{props.isMeasuring && (
				<>
					<button class="coords-panel" onClick={copyDistanceToClipboard}>
						<span>
							<img alt="End Marker" src="/leaflet/marker-icon.png" class="marker start" /> Start:{" "}
							{props.startCoords && formatLatLng(props.startCoords, LatLngFormatType.DegreesDecimalMinutes)}
							{!props.startCoords && "Click anywhere to set a start point."}
						</span>
						{props.startCoords && (
							<span>
								<img alt="Start Marker" src="/leaflet/marker-icon.png" class="marker end" /> End:{" "}
								{props.endCoords && formatLatLng(props.endCoords, LatLngFormatType.DegreesDecimalMinutes)}
								{!props.endCoords && "Click anywhere to set an end point."}
							</span>
						)}
						{props.distanceText && (
							<span style={{ flex: "100%" }}>
								<Icon icon={faRuler} /> {props.distanceText}
							</span>
						)}
					</button>
					{isCopying.value && (
						<div class="copy-toast">
							<ShadowContainer>Copied to clipboard!</ShadowContainer>
						</div>
					)}
				</>
			)}
		</div>
	)
}

export default defineComponent(MeasureDistance, inferProps<Props>())
